/**
 * This file contains the function that creates a Pivot Table renderer
 * to correctly show a Pivot Table as a HTML table.
 * This renderer is used by the Pivot Table component, and the based code was
 * took from their default Table renderer (https://github.com/plotly/react-pivottable/blob/master/src/TableRenderers.jsx)
 * Modifications applied:
 * - Use Custom Trevor cell formatters instead of the formatters provided by
 *	the aggregator (https://github.com/nicolaskruchten/pivottable/wiki/Aggregators)
 *
 */

import React from 'react';
import { PivotData } from 'react-pivottable/Utilities';
import { spanSize } from '../../util/helpers/pivot-helpers';

class TableRenderer extends React.PureComponent {
  render() {
    const pivotData = new PivotData(this.props);
    const colAttrs = pivotData.props.cols;
    const rowAttrs = pivotData.props.rows;
    const rowKeys = pivotData.getRowKeys();
    const colKeys = pivotData.getColKeys();
    const customFormatters = this.props.customFormatters;
    const grandTotalAggregator = pivotData.getAggregator([], []);
    const formatters = [];
    for (const name in pivotData.props.data[0]) {
      const nameIndex =
        rowAttrs.indexOf(name) >= 0
          ? rowAttrs.indexOf(name)
          : colAttrs.indexOf(name) >= 0
          ? rowAttrs.length + colAttrs.indexOf(name)
          : rowAttrs.length + colAttrs.length;
      formatters[nameIndex] = customFormatters[name];
    }

    const getClickHandler =
      this.props.tableOptions && this.props.tableOptions.clickCallback
        ? (value, rowValues, colValues) => {
            const filters = {};
            for (const i of Object.keys(colAttrs || {})) {
              const attr = colAttrs[i];
              if (colValues[i] !== null) {
                filters[attr] = colValues[i];
              }
            }
            for (const i of Object.keys(rowAttrs || {})) {
              const attr = rowAttrs[i];
              if (rowValues[i] !== null) {
                filters[attr] = rowValues[i];
              }
            }
            return (e) =>
              this.props.tableOptions.clickCallback(
                e,
                value,
                filters,
                pivotData,
              );
          }
        : null;

    const getTotalsFormatter = () => {
      return customFormatters[this.props.vals[0]];
    };

    return (
      <table className="pvtTable">
        <thead>
          {colAttrs.map(function (c, j) {
            return (
              <tr key={`colAttr${j}`}>
                {j === 0 && rowAttrs.length !== 0 && (
                  <th colSpan={rowAttrs.length} rowSpan={colAttrs.length} />
                )}
                <th className="pvtAxisLabel">{c}</th>
                {colKeys.map(function (colKey, i) {
                  const x = spanSize(colKeys, i, j);
                  const value = customFormatters[c]
                    ? customFormatters[c](colKey[j])
                    : colKey[j];
                  if (x === -1) {
                    return null;
                  }
                  return (
                    <th
                      className="pvtColLabel"
                      key={`colKey${i}`}
                      colSpan={x}
                      rowSpan={
                        j === colAttrs.length - 1 && rowAttrs.length !== 0
                          ? 2
                          : 1
                      }
                    >
                      {value}
                    </th>
                  );
                })}

                {j === 0 && (
                  <th
                    className="pvtTotalLabel"
                    rowSpan={colAttrs.length + (rowAttrs.length === 0 ? 0 : 1)}
                  >
                    Totals
                  </th>
                )}
              </tr>
            );
          })}

          {rowAttrs.length !== 0 && (
            <tr>
              {rowAttrs.map(function (r, i) {
                return (
                  <th className="pvtAxisLabel" key={`rowAttr${i}`}>
                    {r}
                  </th>
                );
              })}
              <th className="pvtTotalLabel">
                {colAttrs.length === 0 ? 'Totals' : null}
              </th>
            </tr>
          )}
        </thead>

        <tbody>
          {rowKeys.map(function (rowKey, i) {
            const totalAggregator = pivotData.getAggregator(rowKey, []);
            return (
              <tr key={`rowKeyRow${i}`}>
                {rowKey.map(function (txt, j) {
                  const x = spanSize(rowKeys, i, j);
                  const formatter = formatters[j];
                  const value = formatter ? formatter(txt) : txt;
                  if (x === -1) {
                    return null;
                  }
                  return (
                    <th
                      key={`rowKeyLabel${i}-${j}`}
                      className="pvtRowLabel"
                      rowSpan={x}
                      colSpan={
                        j === rowAttrs.length - 1 && colAttrs.length !== 0
                          ? 2
                          : 1
                      }
                    >
                      {value}
                    </th>
                  );
                })}
                {colKeys.map(function (colKey, j) {
                  const aggregator = pivotData.getAggregator(rowKey, colKey);
                  const formatter = getTotalsFormatter();
                  const value = formatter(aggregator.value());
                  const cellContent = !aggregator.value() ? 'NULL' : value;
                  const className = !aggregator.value()
                    ? 'pvtVal pvtValEmpty'
                    : 'pvtVal';

                  return (
                    <td
                      className={className}
                      key={`pvtVal${i}-${j}`}
                      onClick={
                        getClickHandler &&
                        getClickHandler(aggregator.value(), rowKey, colKey)
                      }
                    >
                      {cellContent}
                    </td>
                  );
                })}
                <td
                  className="pvtTotal"
                  onClick={
                    getClickHandler &&
                    getClickHandler(totalAggregator.value(), rowKey, [null])
                  }
                >
                  {getTotalsFormatter()(totalAggregator.value())}
                </td>
              </tr>
            );
          })}

          <tr>
            <th
              className="pvtTotalLabel"
              colSpan={rowAttrs.length + (colAttrs.length === 0 ? 0 : 1)}
            >
              Totals
            </th>

            {colKeys.map(function (colKey, i) {
              const totalAggregator = pivotData.getAggregator([], colKey);
              return (
                <td
                  className="pvtTotal"
                  key={`total${i}`}
                  onClick={
                    getClickHandler &&
                    getClickHandler(totalAggregator.value(), [null], colKey)
                  }
                >
                  {getTotalsFormatter()(totalAggregator.value())}
                </td>
              );
            })}

            <td
              onClick={
                getClickHandler &&
                getClickHandler(grandTotalAggregator.value(), [null], [null])
              }
              className="pvtGrandTotal"
            >
              {getTotalsFormatter()(grandTotalAggregator.value())}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }
}

export default TableRenderer;
